<template>
  <v-container>
    <v-row class="d-flex align-center">
      <v-col md="6" cols="12">
        <h1 class="mb-0">State Packets</h1>
      </v-col>
      <v-col md="6" cols="12">
        <DynamicFormControl
          :control="{
            name: 'filterTerm',
            type: 'TextInput',
            value: filterTerm,
            config: {
              label: 'Find a State Packet',
              rules: '',
              short: true,
              clearable: true,
              prependIcon: 'fas fa-search'
            }
          }"
          @change="updateFilterTerm"
        />
      </v-col>
    </v-row>

    <v-row class="mt-0" v-if="!stateDocumentsDisplay.length">
      <v-col cols="12">
        <br>
        <p>No state packets found matching {{filterTerm}}.</p>
      </v-col>
    </v-row>
    <v-row class="mt-0" v-else>
      <v-col xl="3" lg="4" md="6" cols="12" v-for="doc in stateDocumentsDisplay" :key="doc.path">
        <v-card class="pa-3 mb-1">
          <a :href="documentPath(doc)" target="_blank">
            <v-row class="py-0 d-flex align-center">
              <v-col cols="1">
                <i v-if="getIcon(doc.path)"
                  class="fa mr-2"
                  style="display: inline; font-size: 24px; vertical-align: middle;"
                  :class="getIcon(doc.path).icon"
                  :style="{'color': getIcon(doc.path).color}">
                </i>
              </v-col>
              <v-col cols="11">
                {{doc.name}}
              </v-col>
            </v-row>
          </a>
        </v-card>
      </v-col>
    </v-row>
    <br><br><br>
  </v-container>
</template>

<script>
import Environment from '@/scripts/environment';
import DynamicFormControl from '@/components/form/DynamicFormControl.vue';

// NOTE: only entries with `type: 'State'` are surfaced on this page. The General
// tab (Informational / Agency / Supplemental documents) now lives in the Help
// Center; those entries are retained here only as a reference for the migration
// and can be deleted once the Help Center collection is confirmed complete.
const documents = [
  {
    name: 'Builders Risk Supplement Application',
    path: 'Builders Risk Supplemental Application.pdf',
    channel: 'Human',
    type: 'Supplemental',
    tab: 'General'
  },
  {
    name: 'Consumer Credit Disclosure',
    path: 'Consumer_Credit_Disclosure.pdf',
    channel: 'Human',
    type: 'Supplemental',
    tab: 'General'
  },
  {
    name: 'Excess Flood Application',
    path: 'Excess_Flood_Application.docx',
    channel: 'Human',
    type: 'Supplemental',
    tab: 'General'
  },
  {
    name: 'Homeowners Application',
    path: 'Acord-80-Homeowner-PL.pdf',
    channel: 'Human',
    type: 'Supplemental',
    tab: 'General'
  },
  {
    name: 'Landslide Supplement',
    path: 'Landslide_Supplement.docx',
    channel: 'Human',
    type: 'Supplemental',
    tab: 'General'
  },
  {
    name: 'Log Home Questionnaire',
    path: 'Log_Home_Questionnaire.pdf',
    channel: 'Human',
    type: 'Supplemental',
    tab: 'General'
  },
  {
    name: 'Secondary Modifiers Supplemental Application',
    path: 'Secondary_Modifiers_Supplemental_Application.pdf',
    channel: 'Human',
    type: 'Supplemental',
    tab: 'General'
  },
  {
    name: 'Short Term Rental Questionnaire',
    path: 'Short Term Rental Questionnaire.pdf',
    channel: 'Human',
    type: 'Supplemental',
    tab: 'General'
  },
  {
    name: 'Supplemental Corporate Named Insured Questionnaire',
    path: 'Supplemental Corporate Named Insured Questionnaire.pdf',
    channel: 'Human',
    type: 'Supplemental',
    tab: 'General'
  },
  {
    name: 'Supplemental Heating Questionnaire',
    path: 'Supplemental_Heating_Questionnaire.pdf',
    channel: 'Human',
    type: 'Supplemental',
    tab: 'General'
  },
  {
    name: 'Unprotected Rating Questionnaire',
    path: 'Unprotected Rating Questionnaire.pdf',
    channel: 'Human',
    type: 'Supplemental',
    tab: 'General'
  },
  {
    name: 'Vacant Questionnaire',
    path: 'Vacant Questionnaire.pdf',
    channel: 'Human',
    type: 'Supplemental',
    tab: 'General'
  },

  {
    name: 'Statement of Diligent Effort',
    path: 'digital/DiligentEffort.pdf',
    channel: 'Digital',
    type: 'Supplemental',
    tab: 'General'
  },
  {
    name: 'Statement of No Loss',
    path: 'digital/StatementOfNoLoss.pdf',
    channel: 'Digital',
    type: 'Supplemental',
    tab: 'General'
  },
  {
    name: 'Agent Of Record',
    path: 'digital/AgentOfRecord.pdf',
    channel: 'Digital',
    type: 'Agency',
    tab: 'General'
  },
  {
    name: 'Bank Change Request',
    path: 'digital/BankChangeRequest.pdf',
    channel: 'Digital',
    type: 'Agency',
    tab: 'General'
  },

  {
    name: 'State Packet - Alaska',
    path: 'State_Packet_-_AK.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Alabama',
    path: 'State_Packet_-_AL.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Arkansas',
    path: 'State_Packet_-_AR.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Arizona',
    path: 'State_Packet_-_AZ.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - California (New Business)',
    path: 'State_Packet_-_CA_NewBusiness.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - California (Renewal)',
    path: 'State_Packet_-_CA_Renewal.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Colorado',
    path: 'State_Packet_-_CO.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Connecticut',
    path: 'State_Packet_-_CT.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - District of Columbia',
    path: 'State_Packet_-_DC.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Delaware',
    path: 'State_Packet_-_DE.pdf',
    type: 'State'
  },
    {
    name: 'State Packet - Delaware (Flood)',
    path: 'State_Packet_-_DE_Flood.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Georgia Appendix A',
    path: 'State_Packet_-_GA_Appendix_A.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Georgia',
    path: 'State_Packet_-_GA.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Hawaii',
    path: 'State_Packet_-_HI.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Iowa',
    path: 'State_Packet_-_IA.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Idaho',
    path: 'State_Packet_-_ID.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Illinois',
    path: 'State_Packet_-_IL.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Indiana',
    path: 'State_Packet_-_IN.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Kansas',
    path: 'State_Packet_-_KS.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Kentucky',
    path: 'State_Packet_-_KY.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Lousiana Personal Lines',
    path: 'State_Packet_-_LA_Personal_Lines.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Louisiana',
    path: 'State_Packet_-_LA.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Massachusetts',
    path: 'State_Packet_-_MA.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Maryland',
    path: 'State_Packet_-_MD.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Maine',
    path: 'State_Packet_-_ME.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Michigan',
    path: 'State_Packet_-_MI.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Minnesota',
    path: 'State_Packet_-_MN.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Missouri',
    path: 'State_Packet_-_MO.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Mississippi',
    path: 'State_Packet_-_MS.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Montana',
    path: 'State_Packet_-_MT.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - North Carolina',
    path: 'State_Packet_-_NC.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - North Dakota',
    path: 'State_Packet_-_ND.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Nebraska',
    path: 'State_Packet_-_NE.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - New Hampshire',
    path: 'State_Packet_-_NH.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - New Jersey',
    path: 'State_Packet_-_NJ.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - New Mexico',
    path: 'State_Packet_-_NM.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Nevada',
    path: 'State_Packet_-_NV.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - New York',
    path: 'State_Packet_-_NY.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Ohio',
    path: 'State_Packet_-_OH.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Oklahoma',
    path: 'State_Packet_-_OK.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Oregon',
    path: 'State_Packet_-_OR.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Pennsylvania',
    path: 'State_Packet_-_PA.pdf',
    type: 'State'
  },
    {
    name: 'State Packet - Pennsylvania (Flood)',
    path: 'State_Packet_-_PA_Flood.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Rhode Island',
    path: 'State_Packet_-_RI.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - South Carolina',
    path: 'State_Packet_-_SC.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - South Dakota',
    path: 'State_Packet_-_SD.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Tennessee',
    path: 'State_Packet_-_TN.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Texas',
    path: 'State_Packet_-_TX.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - US Virgin Islands',
    path: 'State_Packet_-_USVI.PDF',
    type: 'State'
  },
  {
    name: 'State Packet - Utah',
    path: 'State_Packet_-_UT.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Virginia',
    path: 'State_Packet_-_VA.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Vermont',
    path: 'State_Packet_-_VT.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Washington',
    path: 'State_Packet_-_WA.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Wisconsin',
    path: 'State_Packet_-_WI.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - West Virginia',
    path: 'State_Packet_-_WV.pdf',
    type: 'State'
  },
  {
    name: 'State Packet - Wyoming',
    path: 'State_Packet_-_WY.pdf',
    type: 'State'
  },
  {
    name: 'Flood - California - D1',
    path: 'State_Packet_-_CA.pdf',
    type: 'State'
  },
  {
    name: 'Frequently Asked Questions',
    path: 'AmwinsIQ_PersonalLines_FAQ.pdf',
    type: 'Informational',
    tab: 'General'
  },
  {
    // If this file name is changed, update accordingly on Catastrophe Closings page
    name: ' Amwins IQ Personal Lines Overview',
    path: 'AmwinsIQ_PersonalLines_Overview.pdf',
    type: 'Informational',
    tab: 'General'
  },
  {
    name: 'Amwins IQ Personal Lines Alpha Overview',
    path: 'AmwinsIQ_PersonalLines_DigitalPolicyResources.pdf',
    type: 'Informational',
    tab: 'General'
  },
  {
    name: 'Amwins IQ Homeowners Cancellation Guidelines',
    path: 'AmwinsIQ_Homeowners_Cancellation_Guidelines.pdf',
    type: 'Informational',
    tab: 'General'
  },
  {
    name: 'Residential Flood Coverage Comparison',
    path: 'flood/AmwinsIQ_Flood_CoverageComparison.pdf',
    type: 'Informational',
    tab: 'General'
  },
  {
    name: 'Flood Frequently Asked Questions',
    path: 'flood/AmwinsIQ_FloodMarketplaceFAQ.pdf',
    type: 'Informational',
    tab: 'General'
  },
  {
    name: 'Flood Product Overview',
    path: 'flood/AmwinsIQ_FloodProductOverview.pdf',
    type: 'Informational',
    tab: 'General'
  },
  {
    name: 'FloodPlus Residential Guide',
    path: 'flood/AmwinsIQ_FloodPlusResidentialGuide.pdf',
    type: 'Informational',
    tab: 'General'
  }, {
    name: 'Amwins IQ Personal Lines Vave Overview',
    path: 'AmwinsIQ_PersonalLinesVaveOverview.pdf',
    type: 'Informational',
    tab: 'General'
  }, {
    name: 'Amwins IQ Personal Lines Munich Overview',
    path: 'AmwinsIQ_PersonalLinesMunichOverview.pdf',
    type: 'Informational',
    tab: 'General'
  }

];

export default {
  name: 'StatePackets',
  components: {
    DynamicFormControl
  },
  data () {
    return {
      documents: [],
      stateDocumentsDisplay: [],
      filterTerm: null,
      timeout: null
    };
  },
  methods: {
    documentPath (doc) {
      return `${Environment.CDN_HOST}/system-documents/${doc.path}`;
    },
    getIcon (path) {
      if (!path) return null;
      path = path.toLowerCase();
      if (path.indexOf('.pdf') > -1) {
        return { color: 'red', icon: 'fa-file-pdf' };
      } else if (path.indexOf('.doc') > -1) {
        return { color: 'rgb(68, 136, 227)', icon: 'fa-file-word' };
      } else {
        return null;
      }
    },
    updateFilterTerm (event) {
      if (event) {
        this.filterTerm = event.value;
      } else {
        this.filterTerm = null;
      }

      if (this.timeout) clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        this.updateDocumentsDisplay();
      }, 200);
    },
    updateDocumentsDisplay () {
      if (!this.filterTerm) {
        this.stateDocumentsDisplay = [...this.documents.filter(x => x.type === 'State')];
      } else {
        const f = this.filterTerm.toLowerCase();
        this.stateDocumentsDisplay = [...this.documents.filter(d => d.type === 'State' && (d.name.toLowerCase().indexOf(f) > -1 || d.path.toLowerCase().indexOf(f) > -1))];
      }
    }
  },
  computed: {
  },
  mounted () {
    this.documents = documents.sort((a, b) => (a.name > b.name) ? 1 : -1);
    this.stateDocumentsDisplay = [...this.documents.filter(x => x.type === 'State')];
  }
};
</script>

<style scoped lang="scss">
  @import '@/style.scss';
  a {
    text-decoration: none;
    display: block;
    width: 100%;
  }
  a:hover {opacity: 0.8;}
</style>

